import React, { useEffect } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { InputAdornment, Typography } from '@mui/material';
import parse from 'html-react-parser';
import { Controller, useFormContext } from 'react-hook-form';

import BlankTextField from './blankTextField';
import { CorrectAnswerBox, QuestionContainer } from './style';

import { IAnswer, IQuiz, IUserAnswer } from '@interfaces/learning';
import { sanitize } from 'src/helpers/utils';
import theme from 'src/theme';

export type FillInBlankFormProps = {
  content: IQuiz;
  answers: IAnswer[] | null;
  userAnswers?: IUserAnswer[];
  showAnswer?: boolean;
  isLoading?: boolean;
};

export type InteractiveFillBlankFields = {
  answers: Record<string | number, string>;
};

const FillInBlankForm = ({ content, answers, userAnswers, showAnswer, isLoading }: FillInBlankFormProps) => {
  const questionSet = content.question_sets?.[0];
  const question = questionSet?.question_sets_questions?.[0]?.content!;

  const {
    control,
    reset,
    formState: { errors, isSubmitted },
  } = useFormContext<InteractiveFillBlankFields>();

  useEffect(() => {
    reset({
      answers: question?.answers.reduce((acc, answer) => {
        if (answer.group_name && question.name?.includes(`{{${answer.group_name}}}`))
          return { ...acc, [answer.group_name]: '' };
        return acc;
      }, {}),
    });
  }, [question?.answers, question?.name, reset]);

  const renderQuestion = (dirtyHtml: string, blanks: IAnswer[]) => {
    const regex = /{{[^{}]+}}|.+?(?={{[^{}]+}})|.+/g;
    const { __html: sanitizedHtml } = sanitize(dirtyHtml);

    return parse(sanitizedHtml, {
      replace: (domNode) => {
        if (domNode.type === 'text') {
          const matches = ((domNode as unknown) as Text).data?.match(regex);
          const children = matches?.map((match) => {
            const blank = blanks.find((answer) => `{{${answer.group_name}}}` === match);
            if (blank) {
              return (
                <Controller
                  name={`answers.${blank.group_name}`}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    let icon: React.ReactNode | undefined;

                    const questionAnswers = answers?.reduce((acc, answer) => {
                      if (answer.group_name === blank.group_name) {
                        acc.push(answer.description);
                      }
                      return acc;
                    }, [] as string[]);

                    const userAnswer = userAnswers?.find((userAnswer) => userAnswer.group_name === blank.group_name);
                    const isCorrect = userAnswer?.is_correct;

                    if (userAnswers) {
                      icon = (
                        <InputAdornment position="end">
                          {!isCorrect ? (
                            <ClearIcon sx={{ color: theme.palette.error.main }} />
                          ) : (
                            <CheckIcon sx={{ color: theme.palette.success.main }} />
                          )}
                        </InputAdornment>
                      );
                    }

                    return (
                      <>
                        <BlankTextField
                          isError={isSubmitted && !isLoading && !isCorrect}
                          isCorrect={isSubmitted && !isLoading && isCorrect}
                          disabled={isSubmitted}
                          isLoading={isLoading}
                          InputProps={{
                            endAdornment: icon,
                          }}
                          renderCorrectAnswer={(boxWidth: number) =>
                            showAnswer &&
                            fieldState.error &&
                            questionAnswers?.length && (
                              <CorrectAnswerBox width={boxWidth}>{questionAnswers.join(', ')}</CorrectAnswerBox>
                            )
                          }
                          {...field}
                        />
                      </>
                    );
                  }}
                />
              );
            }
            return (
              <Typography component="span" fontSize={24}>
                {match}
              </Typography>
            );
          });

          return <>{children}</>;
        }

        return domNode;
      },
    });
  };

  return <QuestionContainer>{renderQuestion(question?.name, question?.answers)}</QuestionContainer>;
};

export default FillInBlankForm;
